import React, { useEffect, useState, useRef, useMemo } from 'react'
import {
  Accordion, AccordionDetails, AccordionSummary, Box, Button, CircularProgress,
  Dialog, Divider, Grid, IconButton, Stack, Tooltip, Typography,
} from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import EditIcon from '@mui/icons-material/Edit'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import moment from 'moment'
import Draggable from "react-draggable"
import InfoIcon from '@mui/icons-material/Info'
import stylesheet from '../detail-container.stylesheet'
import QuestionnaireComponent from '../../../questionnaires/questionnaire.component'
import CardLayoutComponent from '../common/card-layout.component'
import NotesComponent from '../common/notes.component'
import { RootState, useAppThunkDispatch } from '../../../../redux/store'
import ReferralComponent from '../common/referral-card.component'
import { useReferralAction } from '../../../../utils/referral-hook'
import { useOverrideAction } from '../../../../utils/override-hook'
import EmptyCardComponent from "../common/empty-card.component"
import Helper from '../../../../utils/helper'
import ManualOverrideComponent from "../common/manual-override.component"
import CategoryQuesionnariesComponent from '../../../questionnaires/category-quesionnaries.component'
import ColorBubbleComponent from '../common/color-bubble.component'
import { useUpdateDataAction } from '../../../../utils/update-details-hook'
import { updatePatientDetail } from '../../../../redux/patient-detail-slice'
import { formatDisplayName, hasDifferentPrefix } from '../../../../utils/display-name-formatter'
import AddIcon from '@mui/icons-material/Add'
import TrackSymptomPickerComponent from '../common/track-symptom-picker.component'

interface IProps {
  input: any
  classes: ClassNameMap
}

const CalculatorsComponent: React.FC<IProps> = ({ input, classes }) => {
  const [data, setData] = useState([] as any)
  const [recommendedData, setRecommendedData] = useState([] as any)
  const [category, setCategory] = useState([] as any)
  const [open, setOpen] = useState(false)
  const [openCategory, setOpenCategory] = useState(false)
  const [currCategory, setCurrCategory] = useState({} as any)
  const [selectedCal, setSelectedCal] = useState({} as any)
  const [isSubmit, setIsSubmit] = useState(false)
  const [isConfirmSubmit, setIsConfirmSubmit] = useState(false)
  const [selectedNotes, setSelectedNotes] = useState([] as number[])

  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)
  const selectedProgram = useSelector((state: RootState) => state.commonData.selectedProgram)
  const program_enrollments = useSelector((state: RootState) => state.patientDetail.patientDetail?.program_enrollments || [])
  const recommended_calculators = useSelector((state: RootState) => state.commonData.recommended_calculators)
  const uiResource = useSelector((state: RootState) => state.metaData.metadata)

  const { onSetView, onDataSubmit } = useReferralAction()
  const { onSetOverView, onDataOverSubmit } = useOverrideAction()
  const inputEl = useRef(null) as any
  const calRef = useRef(null) as any
  const isInitialMount = useRef(true)
  const { updatePatientData } = useUpdateDataAction(patientDetail.id)
  const dispatch = useAppThunkDispatch()
  const [trackPickerOpen, setTrackPickerOpen] = useState(false)
  // Preserve batch-save results across Redux refreshes.
  // batchSaveQuestionnaires does not update measurements.calculators in the backend,
  // so updatePatientData would revert chip state without this overlay.
  const localBatchOverridesRef = useRef<Map<string, any>>(new Map())

  // Combine data for rendering and memoization
  const combinedData = useMemo(() => {
    return [...data, ...recommendedData]
  }, [data, recommendedData])

  // Memoized lookup map for O(1) ID access
  const dataMap = useMemo(() => {
    const map = new Map()
    combinedData?.forEach((item: any) => {
      map.set(item.id, item)
    })
    return map
  }, [combinedData])

  // Memoized category-based filtering
  const dataByCategoryMap = useMemo(() => {
    const categoryMap = new Map()
    combinedData?.forEach((item: any) => {
      if (!categoryMap.has(item.category)) {
        categoryMap.set(item.category, [])
      }
      categoryMap.get(item.category).push(item)
    })
    return categoryMap
  }, [combinedData])

  const isRecommended = (id: any) => recommendedData?.some((i: any) => i.id === id)

  const isEvalProgram = (prg: any) => program_enrollments?.some((m: any) =>
    m.program_type === 'evaluation' && m.program === prg
  )

  // Memoized diagnosis data grouped by program and response
  const diagnosisGroupedData = useMemo(() => {
    const grouped = new Map()
    combinedData?.filter((i: any) => i.category === 'diagnosis')?.forEach((item: any) => {
      item.programs?.forEach((prg: string) => {
        if (!grouped.has(prg)) {
          grouped.set(prg, { Yes: [], No: [], Recommended: [], Unknown: [] })
        }

        const prgData = grouped.get(prg)
        const isEvalPrg = isEvalProgram(prg)
        // Normalize first_response to handle both string and number types
        const responseValue = item.first_response !== null && item.first_response !== undefined
          ? (item.first_component_type === 'scale' ? parseFloat(item.first_response) : item.first_response)
          : null

        const isScaleType = item.first_component_type === 'scale'
        const isScalePositive = isScaleType && responseValue !== null && responseValue > 3
        const isScaleNeutral = isScaleType && responseValue === 3
        const isScaleNegative = isScaleType && responseValue !== null && responseValue < 3 && responseValue > 0

        // Helper function to remove item from all groups
        const removeFromAllGroups = (itemId: any) => {
          ['Yes', 'No', 'Recommended', 'Unknown'].forEach(group => {
            prgData[group] = prgData[group].filter((existingItem: any) => existingItem.id !== itemId)
          })
        }

        // Remove existing item with same id from all groups before adding
        removeFromAllGroups(item.id)

        // Normalize 'Unknown' response (case-insensitive)
        const isUnknownResponse = typeof responseValue === 'string' && responseValue.toLowerCase() === 'unknown'

        if (isEvalPrg) {
          if (responseValue === 'Yes' || isScalePositive) {
            prgData.Yes.push(item)
          }
          else if (responseValue === 'No' || isScaleNegative) {
            prgData.No.push(item)
          }
          // Recommended items with no explicit Yes/No answer (null,
          // scale-neutral, or default "Unknown") respect needs_confirmation
          // to decide between Recommended and Unknown buckets. Non-recommended
          // "Unknown" responses fall through and stay hidden — default answer
          // is "Unknown" for most of the 2K+ questionnaires.
          else if (isRecommended(item.id) &&
            (responseValue === null || isScaleNeutral || isUnknownResponse)) {
            if (item.needs_confirmation)
              prgData.Recommended.push(item)
            else
              prgData.Unknown.push(item)
          }
          else if (item.needs_confirmation === false &&
            (responseValue === null || isScaleNeutral) &&
            item?.recent_confirmation !== null
          ) {
            prgData.Unknown.push(item)
          }
        } else {
          // Non-eval programs: same filtering as eval — only show items with
          // explicit answers or that are recommended, not all 2K+ questionnaires
          if (responseValue === 'Yes' || isScalePositive) {
            prgData.Yes.push(item)
          } else if (responseValue === 'No' || isScaleNegative) {
            prgData.No.push(item)
          } else if (isRecommended(item.id)) {
            if (item.needs_confirmation)
              prgData.Recommended.push(item)
            else
              prgData.Unknown.push(item)
          } else if (isUnknownResponse) {
            prgData.Unknown.push(item)
          } else if (
            // Catch unanswered or scale-neutral items that need confirmation
            // (e.g. ROS scale questionnaires with null response or value 3)
            (responseValue === null || (isScaleType && isScaleNeutral)) &&
            (item.needs_confirmation || item?.recent_confirmation !== null)
          ) {
            if (item.needs_confirmation)
              prgData.Recommended.push(item)
            else
              prgData.Unknown.push(item)
          }
        }
      })
    })
    return grouped
  }, [combinedData, recommendedData])

  useEffect(() => {
    initialiseData()
  }, [patientDetail])

  useEffect(() => {
    setCategory(Object.keys(uiResource?.QUES_CATEGORY || {})?.map((_key: any) => uiResource?.QUES_CATEGORY[_key]))
  }, [])

  const initialiseData = () => {
    const calculators = patientDetail?.measurements?.calculators || []
    const recommendedCalculators = patientDetail?.measurements?.recommended_calculators || []
    // Bespoke tracked symptoms (a-la-carte / plan-bridged) — off-program, so they are
    // NOT action items; in the normal panel they join the calc list and group by their
    // own category (e.g. Toxicity) alongside the program tox questionnaires.
    const trackedCalculators = patientDetail?.measurements?.tracked_calculators || []

    let calData = input?.isActionItems === true
      ? calculators
        ?.filter((i: any) => i.needs_confirmation === true &&
          i.programs?.includes(input?.actionProgram) &&
          !i.overridden_programs?.includes(input?.actionProgram))
      // Dedup by name: once a tracked questionnaire is answered, the program sweep
      // also surfaces it in `calculators`, so a naive concat renders it twice until a
      // reload (the server's compute_tracked_calcs excludes program-native ones). Mirror
      // that dedup client-side so the merge is idempotent — no transient duplicate.
      : [...calculators, ...trackedCalculators.filter((t: any) =>
          !calculators?.some((c: any) => c.name === t.name))]

    let recData = input?.isActionItems === true
      ? recommendedCalculators
        ?.filter((i: any) => i.needs_confirmation === true &&
          i.programs?.includes(input?.actionProgram) &&
          !i.overridden_programs?.includes(input?.actionProgram))
      : recommendedCalculators

    setData(
      calData?.map((e: any) => {
        const override = localBatchOverridesRef.current.get(e.id)
        return override ? { ...e, ...override, mode: 'view' } : { ...e, mode: 'view' }
      })
    )

    setRecommendedData(
      recData?.map((e: any) => {
        return { ...e, mode: 'view' }
      })
    )
  }

  const handleClickOpen = (_id: string) => {
    setSelectedCal(dataMap.get(_id))
    setOpen(true)
  }

  const handleCategoryEditOpen = (key: string, diagData = [] as any) => {
    setCurrCategory({
      key: key,
      // Sort by ORIGINAL display_name (with prefix) to preserve grouping
      diagData: diagData?.sort((a: any, b: any) => (formatDisplayName(a?.display_name) || '').toLowerCase().localeCompare((formatDisplayName(b?.display_name) || '').toLowerCase()))
    })
    setOpenCategory(true)
  }

  const handleDiagnosisEditOpen = (prg: any, type = '', diagData = [] as any) => {
    // Debug: Log ALL fields to understand what indicates an "Unknown" answer
    // console.log('[handleDiagnosisEditOpen] Full diagData:', diagData?.map((d: any) => ({
    //   id: d.id,
    //   name: d.name,
    //   first_response: d.first_response,
    //   needs_confirmation: d.needs_confirmation,
    //   recent_confirmation: d.recent_confirmation,
    //   confirmed: d.confirmed,
    //   status: d.status
    // })))
    setCurrCategory({
      key: 'diagnosis',
      program: prg,
      first_response: type,
      // Sort by ORIGINAL display_name (with prefix) to preserve grouping
      diagData: diagData?.sort((a: any, b: any) => (formatDisplayName(a?.display_name) || '').toLowerCase().localeCompare((formatDisplayName(b?.display_name) || '').toLowerCase()))
    })
    setOpenCategory(true)
  }
  const handleCategoryEditClose = (currData: any[]) => {
    // Merge batch-saved values back into local data so catData is fresh on next open.
    // The batch endpoint updates questionnaire records but not measurements.calculators,
    // so updatePatientData alone won't refresh chip state when the dialog reopens.
    if (currData && currData.length > 0) {
      const updatedMap = new Map(currData.map((d: any) => [d.id, d]))
      updatedMap.forEach((val: any, key: string) => {
        if (val.first_response != null) {
          localBatchOverridesRef.current.set(key, {
            first_response: val.first_response,
            needs_confirmation: false,
          })
        }
      })
      const applyUpdates = (prev: any[]) =>
        prev.map((d: any) => updatedMap.has(d.id) ? { ...d, ...updatedMap.get(d.id) } : d)
      setData(applyUpdates)
      setRecommendedData(applyUpdates)
    }
    setCurrCategory({})
    setOpenCategory(false)
  }

  const handleClose = () => {
    setOpen(false)
    setIsSubmit(false)
    setIsConfirmSubmit(false)
    setSelectedCal({})
  }

  const getQuestionnarieName = (id: any) => formatDisplayName(dataMap.get(id)?.display_name) || id

  const handleSave = (_type: string, res: any) => {
    // Reset state
    setOpen(false);
    setIsSubmit(false);
    setIsConfirmSubmit(false);
    setSelectedCal({});

    // Validate response
    if (!res || !res.id) {
      const calName = selectedCal?.id ? getQuestionnarieName(selectedCal.id) : '';
      const action = _type === 'confirm' ? 'confirm' : 'save';
      toast.error(`Failed to ${action} ${calName}.`, Helper.bottom_center());
      return;
    }

    const isEvalPrg = (selectedCal?.programs)?.every((m: any) => isEvalProgram(m))

    if (isEvalPrg) {
      // Get current programs from existing calculator
      const currObjPrg = [...data, ...recommendedData]?.find((i: any) => i.id === res.id)?.programs || []

      // Update diagnosis group with current data state
      const currDiaInfo = Helper.updateDiagnosisGroup(
        { ...res, programs: currObjPrg },
        data,
        recommended_calculators  // FIX: Pass current recommendedData state, not recommended_calculators from Redux
      );

      if (currDiaInfo && Object.keys(currDiaInfo).length > 0) {
        // Use returned data if available, otherwise preserve existing state
        const updatedCalculators = currDiaInfo.calculators ?? data;
        const updatedRecommendedCalculators = currDiaInfo.recommended_calculators ?? recommendedData;

        // Update local state first - this triggers re-render and memo recalculation
        setData(updatedCalculators);
        setRecommendedData(updatedRecommendedCalculators);

        // Update Redux store with the same data
        dispatch(
          updatePatientDetail({
            measurements: {
              ...patientDetail?.measurements,
              calculators: updatedCalculators,
              recommended_calculators: updatedRecommendedCalculators  // FIX: Use updated data
            },
          })
        );
      }
    } else {
      updatePatientData({ isMeasurement: true });
    }
  };

  const handleIsSubmit = (flag: boolean) => setIsSubmit(flag)

  const setIcons = () => {
    return input?.isActionItems === true
      ? []
      : [
        { iconType: 'edit', callback: handleClickOpen },
        { iconType: 'description', callback: onNotesClick },
        { iconType: 'more', callback: {} },
      ]
  }

  const rightHeaderCal = (d: any) => <></>

  const bottomTextCal = (d: any) => {
    return (d.is_referred)
      ? moment.unix(d.referred_on).format("MM/DD/YY")
      : d.created_on
        ? moment.unix(d.created_on).format('MM/DD/YY')
        : ''
  }

  const onNotesClick = (_id: any) => {
    if (selectedNotes.includes(+_id)) {
      setSelectedNotes([...selectedNotes.filter((f) => f !== +_id)])
    } else {
      setSelectedNotes([...selectedNotes, +_id])
    }
  }

  const onRefClick = (_id: any, _mode: string) => {
    setData(onSetView(_id, _mode, data))
    setRecommendedData(onSetView(_id, _mode, recommendedData))
  }

  const onRefSubmit = (_id: any, date: number, is_referred: boolean, referral_id: any) => {
    const updatedData = onDataSubmit(_id, date, is_referred, referral_id, data)
    const updatedRecommendedData = onDataSubmit(_id, date, is_referred, referral_id, recommendedData)

    setData(updatedData)
    setRecommendedData(updatedRecommendedData)
  }

  const onOverrideSubmit = (id: any) => {
    setData(data?.map((el: any) => (el.id === id ? { ...el, mode: 'view' } : el)))
    setRecommendedData(recommendedData?.map((el: any) => (el.id === id ? { ...el, mode: 'view' } : el)))
  }

  const onOverrideClick = (_id: any, _mode: string) => {
    setData(onSetOverView(_id, _mode, data))
    setRecommendedData(onSetOverView(_id, _mode, recommendedData))
  }

  const getDiagnosisRes = (d: any) => {
    // Show Yes, No, or scale values 1,2,4,5 (not 3) - excludes Unknown
    const response = d.first_response
    const isScale = d.first_component_type === 'scale'
    const scaleValue = isScale ? parseFloat(response) : null

    // Show (Yes), (No) or scale values (1), (2), (4), (5) - not (3) or (Unknown)
    const showValue = response === 'Yes' || response === 'No' ||
      (isScale && scaleValue !== null && [1, 2, 4, 5].includes(scaleValue))

    return showValue ? `(${response})` : ''
  }

  const getSeverity = (severity: any, isTitle: string) => {
    return severity && severity?.value !== null ?
      <Typography color="textSecondary" variant="subtitle2" className={classes.subHeaderTitle}>
        {`${isTitle} (severity:${severity?.value}${severity?.max_value ? `/${severity?.max_value}` : ''})`}
        <Tooltip
          data-testid='severity-testId'
          title={severity?.question_text ? severity?.question_text : 'Details not available'}
          placement="bottom">
          <IconButton size='small' className={classes.infoIcon} sx={{ padding: '0px' }}>
            <InfoIcon className={classes.infoIconFont} />
          </IconButton>
        </Tooltip>
      </Typography>
      : ''
  }

  const renderCalculatorCard = (data: any, cat: any, prg = '') => {
    if (!data?.length) return ''

    // Sort by ORIGINAL display_name (with prefix) to preserve grouping
    const sortedData = [...data].sort((a: any, b: any) =>
      (formatDisplayName(a?.display_name) || '').toLowerCase().localeCompare((formatDisplayName(b?.display_name) || '').toLowerCase())
    )

    return sortedData.map((d: any, index: number) => {
      // Check if we need a separator line (prefix changed from previous item)
      const prevItem = index > 0 ? sortedData[index - 1] : null
      const showSeparator = prevItem && hasDifferentPrefix(prevItem.display_name, d.display_name)

      return (
        <React.Fragment key={`calculator-${cat}-${d.id}`}>
          {/* Light separator line when prefix changes */}
          {showSeparator && <Divider sx={{ my: 0.25, borderColor: 'rgba(0, 0, 0, 0.08)', borderBottomWidth: '0.5px' }} />}
          {d.mode === 'view' && (d.programs?.filter((f: string) => selectedProgram.includes(f))?.length > 0 || d.created_by === 'provider_tracked') && (
            <>
              <CardLayoutComponent
                id={d.id}
                name={d?.name}
                leftHeader={formatDisplayName(d?.display_name)}
                rightHeader={rightHeaderCal(d)}
                subHeader={cat === 'diagnosis' ? getDiagnosisRes(d) : getSeverity(d?.severity, '')}
                bottomText={bottomTextCal(d)}
                input={{
                  conditions: d?.cond_per_assessments || [],
                  tests_per_cond: d?.tests_per_cond || [],
                  threshold: Object?.keys(d?.current_rationale || {}) || [],
                }}
                needs_confirmation={d.needs_confirmation || false}
                needs_update={d.needs_update || false}
                icons={setIcons()}
                programs={prg !== '' ? [prg] : d?.programs || []}
                overridenPrograms={d.overridden_programs}
                onRefClick={() => onRefClick(d.id, 'ref')}
                onOverrideClick={() => onOverrideClick(d.id, 'override')}
                is_referred={d.is_referred}
                is_overridden={d.is_overridden || undefined}
                entityRef={'calculator'}
                patientId={patientDetail.id}
                category={cat === 'diagnosis' ? 'conditions' : 'measurements'}
              />
              {selectedNotes.includes(d.id) && <NotesComponent entityId={d.id} entityRef={'calculator'} cancelBtn={true} onCancel={onNotesClick} />}
            </>
          )}
          {d.id === selectedCal?.id && isSubmit && (
            <Grid container direction="row" justifyContent="center" alignItems="center">
              <Box mb={1}>
                <CircularProgress size="2rem" />
              </Box>
            </Grid>
          )}
          {d.mode === 'ref' && (
            <ReferralComponent
              entityRef="calculator"
              input={d}
              patientId={patientDetail.id}
              leftHeader={formatDisplayName(d.display_name)}
              rightHeader={rightHeaderCal(d)}
              programs={d.programs}
              closeRef={() => onRefClick(d.id, 'view')}
              onRefSubmit={onRefSubmit}
            />
          )}
          {d.mode === 'override' && (
            <ManualOverrideComponent
              entityRef="calculator"
              input={d}
              leftHeader={formatDisplayName(d.display_name)}
              rightHeader={rightHeaderCal(d)}
              programs={d.programs}
              closeOverride={() => onOverrideClick(d.id, 'view')}
              onOverrideSubmit={onOverrideSubmit}
            />
          )}
        </React.Fragment>
      )
    })
  }

  const isDataByCategory = (category: any) => {
    return dataByCategoryMap.has(category) &&
      (dataByCategoryMap.get(category)?.length || 0) > 0
  }

  const isPrgUpdate = (cat: any, prg: any, first_response = '') => {
    if (!combinedData || combinedData.length === 0) return false

    // if (cat === 'diagnosis') {
    //   const diagData = diagnosisGroupedData.get(prg)
    //   if (!diagData) return false

    //   if (first_response === 'Yes') {
    //     return diagData.Yes.some((i: any) => i.needs_confirmation)
    //   } else if (first_response === 'No') {
    //     return [...diagData.No, ...diagData.Recommended].some((i: any) => i.needs_confirmation)
    //   } else {
    //     return [...diagData.Yes, ...diagData.No, ...diagData.Unknown, ...diagData.Recommended]
    //       .some((i: any) => i.needs_confirmation)
    //   }
    // }

    const categoryData = dataByCategoryMap.get(cat) || []
    const filteredData = categoryData.filter((i: any) =>
      (i.programs || []).includes(prg)
    )
    return filteredData.some((i: any) => i.needs_confirmation)
  }

  const getPrgDisplayName = (key: any) => program_enrollments.find((e: any) => e.program === key)?.program_display_name || key

  const getQuestionnarieTitle = (currQ: any) => {
    const name = Helper.toCamelCaseFirstWord(currQ && currQ?.display_name ? formatDisplayName(currQ.display_name) : currQ?.name)
    return currQ.category === "diagnosis"
      ? `${name} ${getDiagnosisRes(currQ)}`
      : currQ?.severity && currQ?.severity?.value !== null
        ? getSeverity(currQ?.severity, name)
        : name
  }

  const getDiaByGroup = (group: any, prg: any) => {
    const diagData = diagnosisGroupedData.get(prg)

    if (!diagData) return []
    return diagData[group] || []
  }

  const renderDiagnosisCard = (key: any, prg: any) => {
    const diagData = getDiaByGroup(key, prg) || [] as any
    if (diagData?.lenght < 1) return ''

    return (
      <Box>
        <Accordion square defaultExpanded={key === 'Yes' || input.isActionItems} className={classes.patientAccrodionBg}>
          <AccordionSummary ref={inputEl} expandIcon={<ExpandMoreIcon className={classes.patientAccrodionExpandIcon} />} className={classes.calAccordionSummary}>
            <Stack direction="row" width={'100%'} justifyContent="space-between" alignItems="center">
              <Typography className={classes.subTitle}>{key === 'Unknown' ? 'Unknown/Neutral' : key}</Typography>
              {!input?.actionProgram && diagData?.length > 0 && (
                <Button
                  variant="text"
                  title="Edit"
                  disabled={Helper.isPrgInActive(program_enrollments, [key])}
                  className={classes.expressModeBtn}
                  sx={{
                    padding: '0rem !important',
                    minWidth: '32px !important',
                    color: diagData.some((i: any) => i.needs_confirmation) ? 'var(--color-primary) !important' : '#757575',
                    '&.Mui-disabled': {
                      border: 'none !important',
                    },
                  }}
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleDiagnosisEditOpen(prg, key, diagData)
                  }}
                >
                  <EditIcon className={classes.subHeadername} />
                </Button>
              )}
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Box>{diagData?.length > 0 ? renderNewCategory(diagData, prg) : ''}</Box>
          </AccordionDetails>
        </Accordion>
      </Box>
    )
  }

  const renderNewCategory = (diagData: any, prg: any) => {
    return (<Box>
      {diagData?.length > 0
        ? renderCalculatorCard(diagData, 'diagnosis', prg)
        : <EmptyCardComponent />
      }
    </Box>
    )
  }

  const renderCalculatorsList = (category: any) => {
    // Tracked bespoke symptoms are added via the Toxicity "+", so surface ALL of them
    // (whatever their KB category — TOXICITY or SYMPTOM_EVAL/*__attr) under the Toxicity
    // group where they were added; the "(tox)"/"(attr)" display suffix still distinguishes
    // them. (SYMPTOM_EVAL has no group of its own — it is excluded at the category map.)
    const isTox = String(category).toLowerCase().includes('tox')
    const catData = data?.filter((i: any) =>
      i.category === category || (isTox && i.created_by === 'provider_tracked')
    ) || []
    if(catData?.length < 1)  return null
    
    return (
      <Accordion square defaultExpanded className={classes.patientAccrodionBg}>
        <AccordionSummary ref={inputEl} expandIcon={<ExpandMoreIcon className={classes.patientAccrodionExpandIcon} />} className={classes.calAccordionSummary}>
          <Stack direction="row" width={'100%'} justifyContent="space-between" alignItems="center">
            <Typography className={classes.subTitle}>{Helper.toCamelCaseFirstWord(category)}</Typography>
            <Stack direction="row" alignItems="center">
              {!input?.actionProgram && String(category).toLowerCase().includes('tox') && (
                <Button
                  variant="text"
                  title="Track a symptom"
                  className={classes.expressModeBtn}
                  sx={{ padding: '0rem !important', minWidth: '32px !important', color: '#757575' }}
                  onClick={(e: any) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setTrackPickerOpen(true)
                  }}
                >
                  <AddIcon className={classes.subHeadername} />
                </Button>
              )}
              {!input?.actionProgram && (
                <Button
                  variant="text"
                  title="Edit"
                  className={classes.expressModeBtn}
                  sx={{
                    padding: '0rem !important',
                    minWidth: '32px !important',
                    color: catData.some((i: any) => i.needs_confirmation && !i?.is_overridden) ? 'var(--color-primary) !important' : '#757575',
                  }}
                  onClick={(e: any) => {
                    e.preventDefault()
                    e.stopPropagation()
                    handleCategoryEditOpen(category, catData)
                  }}
                >
                  <EditIcon className={classes.subHeadername} />
                </Button>
              )}
            </Stack>
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container>
            <Grid item className={classes.fullWidth}>
              {renderCalculatorCard(catData, category, '')}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    )
  }

  const renderDiagnosisByProgram = (key: any, index: number) => {
    return (
      <Box sx={{ borderTop: '2px solid rgba(255, 255, 255, 0.15)', borderBottom: '2px solid rgba(255, 255, 255, 0.15)' }}>
        <Accordion square defaultExpanded className={classes.patientAccrodionBg}>
          <AccordionSummary ref={inputEl} expandIcon={<ExpandMoreIcon className={classes.patientAccrodionExpandIcon} />} className={classes.calAccordionSummary}>
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item xs={8}>
                <Typography className={classes.subTitle} variant="body2" sx={{ fontWeight: 600 }}>
                  {getPrgDisplayName(key)}
                  <ColorBubbleComponent selectedProgramList={[key]} />
                </Typography>
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails className={classes.zeroTopPadding}>
            {renderDiagnosisCard('Yes', key)}
            {renderDiagnosisCard('No', key)}
            {renderDiagnosisCard('Recommended', key)}
            {renderDiagnosisCard('Unknown', key)}
          </AccordionDetails>
        </Accordion>
      </Box>
    )
  }

  return (
    <>
      <Grid container direction="row" key={`calculator-${input?.type}`}>
        <Grid item className={classes.fullWidth}>
          {input?.type === 'diagnosis'
            ? <Grid container className={classes.zeroTopPadding}>
              <Grid item className={classes.fullWidth}>
                {combinedData && selectedProgram?.length > 0 && selectedProgram?.map((key: any, index: any) => (
                  <React.Fragment key={`${key}-${index}-${input?.type}`}>
                    {((input?.actionProgram && key === input?.actionProgram && isPrgUpdate('diagnosis', key, ''))
                      || !input?.actionProgram
                    ) &&
                      renderDiagnosisByProgram(key, index)
                    }
                  </React.Fragment>
                ))}
              </Grid>
            </Grid>
            : <>
              {category && category?.length > 0 && category.map((key: any, index: any) => (
                <React.Fragment key={`${key}-${index}-${input?.type}`}>
                  {key !== 'diagnosis' && key !== 'symptom_eval' && isDataByCategory(key) &&
                    renderCalculatorsList(key)
                  }
                </React.Fragment>
              ))}
            </>
          }
        </Grid>
        {combinedData?.length == 0 && input?.isActionItems !== true && <EmptyCardComponent />}
      </Grid>
      {openCategory && (
        <CategoryQuesionnariesComponent
          open={openCategory}
          isBulkEdit={true}
          title={
            currCategory?.key === 'diagnosis'
              ? `Clinical assessments - ${getPrgDisplayName(currCategory?.program)}`
              : Helper.toCamelCaseFirstWord(currCategory?.key)}
          item={currCategory?.diagData || []}
          handleClose={handleCategoryEditClose}
          onDataSaved={(savedData?: any[]) => {
            if (!savedData?.length) return
            const updatedMap = new Map(savedData.map((d: any) => [d.id, d]))
            updatedMap.forEach((val: any, key: string) => {
              if (val.first_response != null) {
                localBatchOverridesRef.current.set(key, { first_response: val.first_response, needs_confirmation: false })
              }
            })
            const applyUpdates = (prev: any[]) =>
              prev.map((d: any) => updatedMap.has(d.id) ? { ...d, ...updatedMap.get(d.id) } : d)
            setData(applyUpdates)
            setRecommendedData(applyUpdates)
          }}
        />
      )}
      {open && (
        <Draggable ref={calRef} defaultPosition={{ x: 280, y: 50 }} handle='.questionnarie-dialog'>
          <Dialog
            id={'cal-id'}
            fullWidth={true}
            maxWidth="md"
            disableEscapeKeyDown
            open={open}
            onClose={handleClose}
            aria-labelledby="calcluator-questionnaire"
            hideBackdrop
            disableScrollLock
            onKeyUp={(e) => {
              e.preventDefault();
              if (e.key === 'Enter')
                setIsConfirmSubmit(true)
            }}
            sx={{
              position: "absolute",
              top: '10%'
            }}
          >
            <QuestionnaireComponent
              data={{
                name: Helper.toCamelCaseFirstWord(formatDisplayName(selectedCal?.display_name) || selectedCal?.name),
                title: getQuestionnarieTitle(selectedCal),
                severity: { ...selectedCal?.severity },
                category: selectedCal.category,
              }}
              pId={patientDetail.id + ''}
              qId={selectedCal?.questionnaire_id + ''}
              qu={selectedCal.id}
              handleIsSubmit={handleIsSubmit}
              handleClose={handleClose}
              handleSave={handleSave}
              isConfirmSubmit={isConfirmSubmit}
              isSubmit={isSubmit}
              needsConfirmation={selectedCal?.needs_confirmation}
              presetIntent={(() => {
                // Backend previews only when nothing is saved, so passing this from the
                // clicked value is safe; shows propagated defaults on open (INV-1).
                const v = String(selectedCal?.first_response ?? '')
                if (['Yes', '5', '4'].includes(v)) return 'confirm'
                if (['No', '1', '2'].includes(v)) return 'deny'
                return undefined
              })()}
            />
          </Dialog>
        </Draggable>
      )}
      <TrackSymptomPickerComponent
        open={trackPickerOpen}
        onClose={() => setTrackPickerOpen(false)}
        patientRefId={patientDetail?.ref_id}
        alreadySurfaced={new Set((data || []).map((d: any) => d.name))}
        onAdded={() => updatePatientData({ isMeasurement: true })}
      />
    </>
  )
}
export default withStyles(stylesheet)(CalculatorsComponent)